import { useCallback, useEffect, useRef } from 'react'

import { GetPatientOptions } from './PatientDetail.types'

/** Interval between consent-sync refetches while waiting for the eConsent webhook. */
export const PTA_CONSENT_SYNC_POLL_INTERVAL_MS = 3000

/**
 * Maximum time to poll for consent attributes after returning from Castor.
 * Covers slower-than-expected webhook delivery (CLOS-13917 may shorten the
 * sender delay; this budget must remain valid either way).
 */
export const PTA_CONSENT_SYNC_MAX_DURATION_MS = 90_000

/** Short delay for a one-shot refresh when consent sync is not pending (e.g. EDC return). */
export const PTA_NON_CONSENT_RETURN_REFRESH_DELAY_MS = 1500

export interface UsePtaPatientDetailsRefreshOnReturnParams {
  isPostTrialAccess: boolean
  isLoading: boolean
  getPatient: (options?: GetPatientOptions) => void
  /**
   * When true, the page polls until consent attributes arrive (or the poll
   * budget expires). When false, tab-return uses a single delayed refresh.
   */
  isConsentSyncPending: boolean
  /**
   * Identity of the patient being synced, as `physicianId:patientId`. React
   * Router reuses this container across patient URLs, so without it a route
   * from one pending patient to another would leave the first patient's
   * interval and deadline in place (or none at all, if that budget had already
   * expired). Changing it restarts the poll on a fresh budget.
   */
  syncKey: string
  pollIntervalMs?: number
  maxPollDurationMs?: number
  nonConsentReturnRefreshDelayMs?: number
}

/**
 * CLOS-13866 / CLOS-13916 / CLOS-13951 - Synchronize PTA patient details after
 * consent is captured in Castor eConsent.
 *
 * CLOS-13916: A single refresh on route entry is wrong, because the eConsent
 * webhook is asynchronous (~20s) and has not arrived yet. Instead the page
 * polls, silently, until the webhook-driven consent attributes appear or the
 * bounded budget expires. Two entry points start that poll:
 *
 *   1. Tab-return (visibilitychange), for consent captured via the in-page
 *      Castor deep link, which opens in a new tab. This leads with an immediate
 *      refetch to cover the fast-webhook path.
 *   2. Route entry / consent-sync becoming pending while the page is open, for
 *      consent captured elsewhere (Oracle APEX, a second window). This does NOT
 *      lead with a refetch: patient details have only just been fetched, and an
 *      immediate second call is the premature route-entry refresh this ticket
 *      was raised about. The first poll lands one interval later.
 *
 * CLOS-13951: Always refetch silently (no page-level spinner), and never commit
 * visible UI updates until webhook-driven PTA fields actually change, otherwise
 * returning from eConsent before consent looks like a premature page refresh.
 */
export function usePtaPatientDetailsRefreshOnReturn({
  isPostTrialAccess,
  isLoading,
  getPatient,
  isConsentSyncPending,
  syncKey,
  pollIntervalMs = PTA_CONSENT_SYNC_POLL_INTERVAL_MS,
  maxPollDurationMs = PTA_CONSENT_SYNC_MAX_DURATION_MS,
  nonConsentReturnRefreshDelayMs = PTA_NON_CONSENT_RETURN_REFRESH_DELAY_MS
}: UsePtaPatientDetailsRefreshOnReturnParams): void {
  const getPatientRef = useRef(getPatient)
  const isLoadingRef = useRef(isLoading)
  const isConsentSyncPendingRef = useRef(isConsentSyncPending)
  const pollIntervalIdRef = useRef<ReturnType<typeof setInterval> | undefined>()
  const oneShotTimeoutIdRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >()
  const pollDeadlineRef = useRef<number | undefined>()

  useEffect(() => {
    getPatientRef.current = getPatient
  }, [getPatient])

  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])

  useEffect(() => {
    isConsentSyncPendingRef.current = isConsentSyncPending
  }, [isConsentSyncPending])

  const clearOneShot = useCallback(() => {
    if (oneShotTimeoutIdRef.current) {
      clearTimeout(oneShotTimeoutIdRef.current)
      oneShotTimeoutIdRef.current = undefined
    }
  }, [])

  const stopPolling = useCallback(() => {
    if (pollIntervalIdRef.current) {
      clearInterval(pollIntervalIdRef.current)
      pollIntervalIdRef.current = undefined
    }
    pollDeadlineRef.current = undefined
  }, [])

  const silentRefetchIfReady = useCallback(() => {
    if (isLoadingRef.current) {
      return
    }
    getPatientRef.current({ silent: true })
  }, [])

  /**
   * @param refetchImmediately Lead with a refetch before the first interval
   * tick. True on tab-return (the user has been away, data may be stale);
   * false on route entry (details were just fetched).
   */
  const startConsentSyncPolling = useCallback(
    (refetchImmediately: boolean) => {
      clearOneShot()
      stopPolling()

      pollDeadlineRef.current = Date.now() + maxPollDurationMs

      if (refetchImmediately) {
        silentRefetchIfReady()
      }

      pollIntervalIdRef.current = setInterval(() => {
        if (!isConsentSyncPendingRef.current) {
          stopPolling()
          return
        }

        if (
          pollDeadlineRef.current !== undefined &&
          Date.now() >= pollDeadlineRef.current
        ) {
          stopPolling()
          return
        }

        if (document.visibilityState !== 'visible') {
          return
        }

        silentRefetchIfReady()
      }, pollIntervalMs)
    },
    [
      clearOneShot,
      maxPollDurationMs,
      pollIntervalMs,
      silentRefetchIfReady,
      stopPolling
    ]
  )

  const scheduleNonConsentReturnRefresh = useCallback(() => {
    stopPolling()
    clearOneShot()

    oneShotTimeoutIdRef.current = setTimeout(() => {
      if (document.visibilityState !== 'visible' || isLoadingRef.current) {
        return
      }
      getPatientRef.current({ silent: true })
    }, nonConsentReturnRefreshDelayMs)
  }, [clearOneShot, nonConsentReturnRefreshDelayMs, stopPolling])

  // Entry point 1: returning to the tab (Castor deep link opens in a new tab).
  useEffect(() => {
    if (!isPostTrialAccess) {
      return
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        // Pause work while the tab is hidden; a later return restarts sync.
        clearOneShot()
        stopPolling()
        return
      }

      if (isConsentSyncPendingRef.current) {
        startConsentSyncPolling(true)
        return
      }

      scheduleNonConsentReturnRefresh()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearOneShot()
      stopPolling()
    }
  }, [
    clearOneShot,
    isPostTrialAccess,
    scheduleNonConsentReturnRefresh,
    startConsentSyncPolling,
    stopPolling
  ])

  // Entry point 2 (CLOS-13916): consent sync is pending on the open page, i.e.
  // route entry after consent was captured in APEX or a second window. Polls
  // without a leading refetch, and stops as soon as the attributes arrive.
  //
  // Keyed on syncKey as well as the flags: routing between two pending patients
  // changes neither flag, so without it patient B would inherit patient A's
  // interval and deadline, or get no polling at all once A's budget expired.
  useEffect(() => {
    if (!isPostTrialAccess || !isConsentSyncPending) {
      stopPolling()
      return
    }

    if (document.visibilityState !== 'visible') {
      return
    }

    startConsentSyncPolling(false)
  }, [
    isConsentSyncPending,
    isPostTrialAccess,
    startConsentSyncPolling,
    stopPolling,
    syncKey
  ])
}
